"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import type { Project } from "@/data/portfolio";

type CategoryFilter = "all" | "ppf" | "ceramic" | "tinting";

const filters: Array<{ value: CategoryFilter; label: string }> = [
  { value: "all", label: "Kõik" },
  { value: "ppf", label: "PPF" },
  { value: "ceramic", label: "Keraamiline kaitse" },
  { value: "tinting", label: "Klaaside toonimine" },
];

interface ProjectRelatedSectionProps {
  projects: Project[];
}

export default function ProjectRelatedSection({ projects }: ProjectRelatedSectionProps) {
  const [activeFilter, setActiveFilter] = useState<CategoryFilter>("all");

  const visibleProjects = useMemo(() => {
    if (activeFilter === "all") return projects;
    return projects.filter((project) => project.category === activeFilter);
  }, [activeFilter, projects]);

  return (
    <section className="mt-16 rounded-sm border border-white/5 bg-secondary p-8 sm:p-10 lg:p-12">
      <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-text-main sm:text-3xl">
            Sarnased tööd
          </h2>
          <p className="mt-2 text-sm leading-6 text-text-muted">
            Valige kategooria ja vaadake teile sobivaid projekte.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 sm:gap-3">
          {filters.map((filter) => {
            const isActive = filter.value === activeFilter;

            return (
              <button
                key={filter.value}
                type="button"
                onClick={() => setActiveFilter(filter.value)}
                aria-pressed={isActive}
                className={`rounded-sm px-4 py-2 text-sm font-medium transition-colors duration-300 sm:px-6 sm:py-3 ${
                  isActive
                    ? "bg-accent text-primary"
                    : "border border-white/10 bg-primary/70 text-text-main hover:border-accent/40 hover:bg-accent hover:text-primary"
                }`}
              >
                {filter.label}
              </button>
            );
          })}
        </div>
      </div>

      {visibleProjects.length > 0 ? (
        <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {visibleProjects.map((project) => (
            <Link
              key={project.slug}
              href={`/tood/${project.slug}`}
              className="group block rounded-sm border border-white/5 bg-primary p-4 transition-colors duration-300 hover:border-accent/40"
            >
              <div className="overflow-hidden rounded-sm bg-primary">
                <div className="relative h-44">
                  <Image
                    src={project.images.after}
                    alt={project.alt}
                    fill
                    unoptimized
                    className="object-cover grayscale-[60%] brightness-90 transition-[filter,transform] duration-300 ease-out group-hover:grayscale-0 group-hover:brightness-100 group-hover:scale-105 group-active:grayscale-0 group-active:brightness-100 group-active:scale-105"
                  />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <h3 className="text-lg font-semibold text-text-main transition-colors group-hover:text-accent">
                  {project.title}
                </h3>
                <p className="text-sm leading-6 text-text-muted">{project.shortDescription}</p>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="mt-8 rounded-sm border border-dashed border-white/10 bg-primary/60 p-8 text-center text-sm text-text-muted">
          Selles kategoorias pole hetkel veel näidiseid.
        </div>
      )}
    </section>
  );
}
